import * as React from "react";

import LocalStore from "#shared-package/local-store.ts";

import Expandable from "./expandable.tsx";
import ToggleButton from "./toggle-button.tsx";

// This context is used in <NavLink /> to determine its hierarchy level.
//
// NOTE(mdr): We could've gone a bit more thorough with this abstraction:
//     created a general concept of "nav level", and set it in <NavSection />.
//     We could even use that strategy to merge <NavSection /> and
//     <NavSubsection /> into a single component, and determine styles entirely
//     based on the level in the nav tree. But idk, that seemed like a bit too
//     much abstraction when all we really needed was a single change in nav
//     link styles :/ Consider revisiting if our hierarchy needs become more
//     complex!
// @ts-expect-error - TS2322 - Type 'Context<{ isInSubsection: false; }>' is not assignable to type 'Context<{ isInSubsection: boolean; }>'.
export const NavSubsectionContext: React.Context<{
    isInSubsection: boolean;
}> = React.createContext({
    isInSubsection: false,
});
NavSubsectionContext.displayName = "NavSubsectionContext";

type Props = {
    // The section contents. Can be expanded or collapsed by this component.
    children: React.ReactNode;
    // The header label.
    label: React.ReactNode;
    // If true, the subsection will be collapsed when the component mounts.
    // Otherwise, the subsection will be expanded when the component mounts.
    initiallyCollapsed?: boolean;
    // Unique Local Storage id for this subsection for storing the state
    // of whether the subsection is open or closed
    localStorageId: string;
    triggerTestId?: string;
    contentTestId?: string;
};

type State = {
    // Whether the content is currently expanded (true) or collapsed (false).
    isExpanded: boolean;
};

const LOCAL_STORAGE_KEY = "navSubsectionState";

const getNavSubsectionState = (
    localStorageId: string,
    defaultValue: boolean,
): boolean => {
    const state = LocalStore.get(LOCAL_STORAGE_KEY);
    if (!state) {
        return defaultValue;
    }
    return typeof state[localStorageId] !== "boolean"
        ? defaultValue
        : state[localStorageId];
};

const setNavSubsectionState = (
    localStorageId: string,
    value: boolean,
): void => {
    const state: any = LocalStore.get(LOCAL_STORAGE_KEY) || {};
    state[localStorageId] = value;
    LocalStore.set(LOCAL_STORAGE_KEY, state);
};

/**
 * A second-level navigation section, to be used inside a NavSection.
 * Expands/collapses its contents.
 */
class NavSubsection extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            isExpanded: getNavSubsectionState(
                props.localStorageId,
                !props.initiallyCollapsed,
            ),
        };
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        const prevIsExpanded = getNavSubsectionState(
            prevProps.localStorageId,
            !prevProps.initiallyCollapsed,
        );
        const isExpanded = getNavSubsectionState(
            this.props.localStorageId,
            !this.props.initiallyCollapsed,
        );
        if (prevIsExpanded !== isExpanded) {
            /**
             * As per the React documentation, it is perfectly fine to call
             * setState from componentDidUpdate as long as it is guarded
             * with a condition that does not rely on the state being set.
             *
             * https://reactjs.org/docs/react-component.html#static-getderivedstatefromprops
             * > If you need to perform a side effect (for example, data
             * > fetching or an animation) in response to a change in props,
             * > use componentDidUpdate lifecycle instead.
             *
             * https://reactjs.org/docs/react-component.html#componentdidupdate
             * > You may call setState() immediately in componentDidUpdate()
             * > but note that it must be wrapped in a condition, or you’ll
             * > cause an infinite loop.
             */

            this.setState({
                isExpanded,
            });
        }
    }

    handleClickButton: () => void = () => {
        const {isExpanded} = this.state;
        setNavSubsectionState(this.props.localStorageId, !isExpanded);
        this.setState({isExpanded: !isExpanded});
    };

    render(): React.ReactNode {
        const {children, label, triggerTestId, contentTestId} = this.props;
        const {isExpanded} = this.state;

        return (
            <li>
                <ToggleButton
                    label={label}
                    isExpanded={isExpanded}
                    onClick={this.handleClickButton}
                    testId={triggerTestId}
                />
                <Expandable isExpanded={isExpanded}>
                    <ul data-testid={contentTestId}>
                        <NavSubsectionContext.Provider
                            value={{isInSubsection: true}}
                        >
                            {children}
                        </NavSubsectionContext.Provider>
                    </ul>
                </Expandable>
            </li>
        );
    }
}

export default NavSubsection;
